import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box } from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import Skeleton from '@material-ui/lab/Skeleton';
import useThemeBreakpoint from '~/modules/common/hooks/useThemeBreakpoint';
import { getBenchTimelinePeriodCount } from './config/benchTimelineChartConfig';

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: theme.spacing(3),
    position: 'relative',
    width: '100%',
    [theme.breakpoints.down('sm')]: {
      gap: theme.spacing(2)
    }
  },
  periodColumn: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    flex: 1,
    minWidth: 0,
    gap: theme.spacing(1),
    padding: theme.spacing(1, 2, 0)
  },
  barContainer: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    height: theme.spacing(4),
    gap: 0,
    marginBottom: theme.spacing(0.5),
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: theme.spacing(0.5),
    overflow: 'hidden'
  },
  barWrapper: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative'
  },
  skeletonBar: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    backgroundColor: theme.palette.action.hover
  }
}));

const PeriodColumnSkeleton = ({ index = 0 }) => {
  const classes = useStyles();

  const heights = useMemo(() => {
    const heightVariants = [
      { bench: 30, overAllocation: 18 },
      { bench: 22, overAllocation: 28 },
      { bench: 28, overAllocation: 24 },
      { bench: 18, overAllocation: 15 },
      { bench: 25, overAllocation: 20 }
    ];

    return heightVariants[index % heightVariants.length];
  }, [index]);

  return (
    <Box className={classes.periodColumn}>
      <Skeleton variant="text" width={80} height={20} />

      <Box className={classes.barContainer}>
        <Box className={classes.barWrapper}>
          <Skeleton
            variant="rect"
            width="100%"
            height={heights.bench}
            className={classes.skeletonBar}
          />
        </Box>

        <Box className={classes.barWrapper}>
          <Skeleton
            variant="rect"
            width="100%"
            height={heights.overAllocation}
            className={classes.skeletonBar}
          />
        </Box>
      </Box>
    </Box>
  );
};

PeriodColumnSkeleton.propTypes = {
  index: PropTypes.number
};

export const BenchOverAllocationTimelineSkeleton = () => {
  const classes = useStyles();
  const theme = useTheme();
  const breakPoint = useThemeBreakpoint(theme);

  const responsivePeriodCount = getBenchTimelinePeriodCount(breakPoint);

  const skeletonItems = useMemo(
    () =>
      Array.from({ length: responsivePeriodCount }, (_, i) => ({
        id: `skeleton-period-${i}`,
        index: i
      })),
    [responsivePeriodCount]
  );

  return (
    <Box className={classes.container}>
      {skeletonItems.map(item => (
        <PeriodColumnSkeleton key={item.id} index={item.index} />
      ))}
    </Box>
  );
};

export default BenchOverAllocationTimelineSkeleton;
